'use client'

import { useEffect, useState } from 'react'

import { Icon } from '@/app/components/Icon'

/**
 * Global component that adds anchor icons to elements with data-anchor-expose="true"
 * Shows anchor icon in bottom right corner with tooltip and copy button on hover
 * Only active in draft/presentation mode
 */
export function AnchorOverlay() {
  const [anchorElements, setAnchorElements] = useState<
    Array<{ element: HTMLElement; anchorId: string }>
  >([])
  const [hoveredAnchor, setHoveredAnchor] = useState<string | null>(null)
  const [isCopied, setIsCopied] = useState(false)
  const [viewportVersion, setViewportVersion] = useState(0)

  // Find all anchor elements and track them
  useEffect(() => {
    const updateAnchorElements = () => {
      const elements = Array.from(document.querySelectorAll('[data-anchor-expose="true"]'))
      const anchorData = elements
        .map((el) => {
          const anchorId = el.getAttribute('id') || el.id
          if (!anchorId) return null
          return { element: el as HTMLElement, anchorId }
        })
        .filter((item): item is { element: HTMLElement; anchorId: string } => item !== null)

      setAnchorElements(anchorData)
    }

    // Initial scan
    updateAnchorElements()

    // Re-scan periodically to catch dynamically added elements
    const interval = setInterval(updateAnchorElements, 1000)

    // Also re-scan on mutations
    const observer = new MutationObserver(updateAnchorElements)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      clearInterval(interval)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    let ticking = false

    const handleViewportChange = () => {
      if (!ticking) {
        ticking = true
        window.requestAnimationFrame(() => {
          setViewportVersion((v) => v + 1)
          ticking = false
        })
      }
    }

    window.addEventListener('scroll', handleViewportChange, { passive: true })
    window.addEventListener('resize', handleViewportChange)

    return () => {
      window.removeEventListener('scroll', handleViewportChange)
      window.removeEventListener('resize', handleViewportChange)
    }
  }, [])

  const handleCopyClick = (anchorId: string) => {
    const anchorUrl = `#${anchorId}`
    const textarea = document.createElement('textarea')
    textarea.value = anchorUrl
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()

    try {
      document.execCommand('copy')
    } finally {
      document.body.removeChild(textarea)
      setIsCopied(true)

      setTimeout(() => {
        setIsCopied(false)
      }, 3000)
    }
  }

  return (
    <>
      {/* read viewportVersion so React knows to re-render on scroll/resize */}
      <span style={{ display: 'none' }}>{viewportVersion}</span>
      {anchorElements.map(({ element, anchorId }) => {
        const rect = element.getBoundingClientRect()
        const isHovered = hoveredAnchor === anchorId

        return (
          <div
            key={anchorId}
            onMouseEnter={() => setHoveredAnchor(anchorId)}
            onMouseLeave={() => setHoveredAnchor(null)}
          >
            {/* Anchor icon - always visible in bottom right */}
            <div
              className="fixed z-[999998] flex items-center justify-center bg-gray-800 text-white rounded w-[24px] h-[24px] cursor-pointer hover:bg-gray-700 transition-colors"
              style={{
                top: rect.bottom - 24,
                right: 8,
              }}
            >
              <Icon name="faAnchor" provider="fontawesome" style="solid" size={12} />
            </div>

            {/* Tooltip with full anchor ID and copy button */}
            {isHovered && (
              <div
                className="fixed z-[999999]"
                style={{
                  top: rect.bottom,
                  right: 8,
                }}
              >
                <div className="mt-1 pl-2 pr-1 py-1 bg-gray-900 text-white text-xs rounded shadow-lg font-mono flex items-center gap-2 whitespace-nowrap">
                  <span>#{anchorId}</span>
                  <button
                    onClick={() => handleCopyClick(anchorId)}
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    title="Copy anchor link"
                  >
                    <Icon
                      name={isCopied ? 'faCheck' : 'faCopy'}
                      provider="fontawesome"
                      style="solid"
                      size={12}
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}
